import { useEffect, useRef, useState } from 'react';

import { IconBoxButton, RefreshIcon, Text } from '@thefarmersfront/kitchen';

import dayjs from 'dayjs';

import { useInterval } from '~/hooks/common';

import styles from './RefreshTimer.module.scss';

const REFRESH_DURATION = 60 * 5; // 5분

interface RefreshTimerProps {
  duration?: number;
  timerKey?: number;
  onRefresh?: () => void | Promise<void>;
}

const RefreshTimer = ({ duration: rawDuration = REFRESH_DURATION, timerKey, onRefresh }: RefreshTimerProps) => {
  const duration = Math.max(1, rawDuration);

  const [endTime, setEndTime] = useState(() => dayjs().valueOf() + duration * 1000);
  const [remainingTime, setRemainingTime] = useState(duration);

  const isRefreshingRef = useRef(false);

  const resetTimer = () => {
    setEndTime(dayjs().valueOf() + duration * 1000);
    setRemainingTime(duration);
  };

  useEffect(() => {
    resetTimer();
    isRefreshingRef.current = false;
  }, [timerKey, duration]);

  const runRefresh = async (callback?: () => void | Promise<void>) => {
    if (isRefreshingRef.current) {
      return;
    }

    isRefreshingRef.current = true;
    try {
      await callback?.();
    } catch (e) {
      console.error(e);
    } finally {
      resetTimer();
      isRefreshingRef.current = false;
    }
  };

  useInterval(() => {
    if (isRefreshingRef.current) {
      return;
    }

    const timeLeft = Math.ceil((endTime - dayjs().valueOf()) / 1000);

    if (timeLeft <= 0) {
      setRemainingTime(0);
      runRefresh(onRefresh);
      return;
    }

    setRemainingTime(timeLeft);
  }, 1000);

  const minutes = Math.floor(remainingTime / 60);
  const seconds = remainingTime % 60;
  const progressPercent = (remainingTime / duration) * 100;

  return (
    <div className={styles.container}>
      <div className={styles.timer}>
        <Text size="large" weight="semi-bold" color="primary">
          {minutes}:{String(seconds).padStart(2, '0')}
        </Text>
        <div className={styles['timer-bar']}>
          <div className={styles['timer-bar-fill']} style={{ width: `${progressPercent}%` }} />
        </div>
      </div>
      <IconBoxButton
        size="tiny"
        variant="contained"
        color="primary"
        aria-label="새로고침"
        onClick={() => runRefresh(onRefresh)}
      >
        <RefreshIcon />
      </IconBoxButton>
    </div>
  );
};

export { RefreshTimer };
